import { useCallback, useState } from 'react';

/**
 * Monitors an element for scroll.
 *
 * @param axis - horizontal/vertical scroll to monitor @default y
 * @param callback - optional callback
 * @returns The hook returns an object containing:
 *   - {boolean} isScrollable - A boolean indicating if the element is scrollable.
 *   - {Function} elementRef - A callback ref function to be assigned to the element being monitored.
 */
export function useElementScrollable(axis: 'x' | 'y' = 'y', callback?: (el: HTMLElement) => void) {
  const [isScrollable, setIsScrollable] = useState<boolean>(false);

  // monitor overflow while resizing
  const elementRef = useCallback((element: HTMLElement) => {
    const resizeObserver = new ResizeObserver(() => {
      setIsScrollable(
        axis === 'y' ? element.offsetHeight < element.scrollHeight : element.offsetWidth < element.scrollWidth,
      );
      callback?.(element);
    });

    if (element !== null) {
      resizeObserver.observe(element);
    } else {
      resizeObserver.disconnect();
    }
  }, []);

  return { isScrollable, elementRef };
}
